/**
 * Chispa de escaneos: los últimos días en miniatura.
 *
 * No busca precisión, busca forma. En la lista lo que se pregunta no es
 * "cuántos escaneos el martes" sino "¿esto tira o está muerto?", y para eso
 * basta la silueta. El detalle de cada día está en la analítica del código.
 *
 * El último día va a plena opacidad y el resto atenuado: sin esa marca, una
 * serie de siete barras iguales no dice cuál es hoy y se lee al revés.
 */

const ALTO = 30;
const HUECO = 2;

export function Chispa({
  valores,
  ancho = 80,
  className = '',
}: {
  readonly valores: readonly number[] | undefined;
  readonly ancho?: number;
  /** Para dejar que crezca con su hueco; el viewBox mantiene la proporcion. */
  readonly className?: string;
}) {
  // Sin datos todavía se pinta el hueco, no nada: si apareciera de golpe al
  // cargar, la ficha entera daría un salto.
  const serie = valores ?? [0, 0, 0, 0, 0, 0, 0];
  const tope = Math.max(1, ...serie);
  const w = (ancho - HUECO * (serie.length - 1)) / serie.length;
  const total = serie.reduce((a, b) => a + b, 0);

  return (
    <svg
      width={ancho}
      height={ALTO}
      viewBox={`0 0 ${ancho} ${ALTO}`}
      className={className}
      role="img"
      aria-label={
        total === 0
          ? 'Sin escaneos en los últimos días'
          : `${total} escaneos en los últimos ${serie.length} días`
      }
    >
      {/* Linea de base: sin ella, una semana a cero son siete rayitas sueltas
          que se leen como un fallo de dibujado en vez de como "medido y
          vacio". Con base, el vacio tiene forma. */}
      <rect
        x="0"
        y={ALTO - 0.75}
        width={ancho}
        height="0.75"
        fill="var(--qr-border)"
      />
      {serie.map((valor, i) => {
        // Las barras vacías conservan un mínimo visible: una fila de ceros
        // tiene que leerse como "medido y vacío", no como "aquí no hay nada".
        const alto = Math.max(1.5, (valor / tope) * (ALTO - 3));
        const ultimo = i === serie.length - 1;
        return (
          <rect
            key={i}
            x={(i * (w + HUECO)).toFixed(2)}
            y={(ALTO - alto).toFixed(2)}
            width={w.toFixed(2)}
            height={alto.toFixed(2)}
            rx="1.2"
            fill="var(--qr-brand)"
            opacity={valor === 0 ? 0.16 : ultimo ? 1 : 0.55}
          />
        );
      })}
    </svg>
  );
}
